import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faGripVertical, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  putCohortAssignmentOrder,
  type TeacherCohortAssignmentRecord,
} from "../auth/api-client";
import { assignmentLifecycleLabel } from "./assignmentLifecycle";
import { CohortAssignmentActionsMenu } from "./CohortAssignmentActionsMenu";
import { formatClosedDateCompactLines, formatDueDateCompactLines } from "./formatDueTimeOfDay";
import { LessonPreviewTile } from "./LessonPreviewTile";
import { PresentationPreviewTile } from "./PresentationPreviewTile";
import { QuizPreviewTile } from "./QuizPreviewTile";
import { SequenceInsertSlot } from "./SequenceInsertSlot";
import { reorderLessonList } from "./TemplateLessonSequenceRail";

export interface CohortLessonPlanRailProps {
  readonly assignments: readonly TeacherCohortAssignmentRecord[];
  readonly cohortId: string | undefined;
  readonly courseId: string;
  readonly bearer: string | null;
  readonly writable: boolean;
  readonly bulkReleaseBusy: boolean;
  readonly selectedAssignmentIds: ReadonlySet<string>;
  readonly selectableAssignments: readonly TeacherCohortAssignmentRecord[];
  readonly isMutating: (id: string) => boolean;
  readonly lessonIconForAssignment: (a: TeacherCohortAssignmentRecord) => {
    iconName?: string | null;
    iconBgColor?: string | null;
    iconFgColor?: string | null;
    workspaceMode?: TeacherCohortAssignmentRecord["workspaceMode"];
  };
  readonly onOrderSaved: () => Promise<void>;
  readonly onError: (message: string | null) => void;
  readonly onToggleSelectAll: () => void;
  readonly onToggleSelected: (id: string) => void;
  readonly onInsert: (insertAfterAssignmentId: string | null | undefined) => void;
  readonly onOpenDueModal: (a: TeacherCohortAssignmentRecord) => void;
  readonly onAssign: (a: TeacherCohortAssignmentRecord) => void;
  readonly onUnassign: (a: TeacherCohortAssignmentRecord) => void;
  readonly onClose: (a: TeacherCohortAssignmentRecord) => void;
  readonly onReopen: (a: TeacherCohortAssignmentRecord) => void;
  readonly onDelete: (a: TeacherCohortAssignmentRecord) => void;
  readonly onCopyStudentLink: (a: TeacherCohortAssignmentRecord) => void;
  readonly onToggleHidden: (a: TeacherCohortAssignmentRecord) => void;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

function assignmentIdsInOrder(items: readonly TeacherCohortAssignmentRecord[]): string[] {
  return items.map((a) => a.id);
}

function ordersMatch(
  a: readonly TeacherCohortAssignmentRecord[],
  b: readonly TeacherCohortAssignmentRecord[]
): boolean {
  const aIds = assignmentIdsInOrder(a);
  const bIds = assignmentIdsInOrder(b);
  return aIds.length === bIds.length && aIds.every((id, i) => id === bIds[i]);
}

function CompactDueLines({ line1, line2 }: { line1: string; line2: string }) {
  return (
    <span className="teacher-cohort-plan-due-compact">
      <span className="teacher-cohort-plan-due-compact-line1">{line1}</span>
      <span className="teacher-cohort-plan-due-compact-line2">{line2}</span>
    </span>
  );
}

function renderDueCell(
  a: TeacherCohortAssignmentRecord,
  writable: boolean,
  mutating: boolean,
  onOpenDueModal: (a: TeacherCohortAssignmentRecord) => void
) {
  if (a.closed) {
    const closedLines = formatClosedDateCompactLines(a.closedAt);
    return closedLines ? (
      <CompactDueLines line1={closedLines.line1} line2={closedLines.line2} />
    ) : (
      <span className="teacher-cohort-plan-due-empty muted">Closed</span>
    );
  }
  if (!a.released) {
    return (
      <span className="teacher-cohort-plan-due-empty muted" aria-hidden>
        -
      </span>
    );
  }
  const dueLines = formatDueDateCompactLines(a.dueAt);
  if (dueLines) {
    return (
      <button
        type="button"
        className="btn secondary tight assignment-due-trigger teacher-cohort-plan-due-btn"
        disabled={!writable || mutating}
        onClick={() => onOpenDueModal(a)}
      >
        <CompactDueLines line1={dueLines.line1} line2={dueLines.line2} />
      </button>
    );
  }
  return (
    <button
      type="button"
      className="btn secondary tight assignment-due-trigger teacher-cohort-plan-due-btn teacher-cohort-plan-due-btn--empty"
      disabled={!writable || mutating}
      onClick={() => onOpenDueModal(a)}
    >
      No due date
    </button>
  );
}

function insertAfterAssignmentIdForGap(
  assignments: readonly TeacherCohortAssignmentRecord[],
  insertBeforeIndex: number
): string | null | undefined {
  if (insertBeforeIndex === 0) return null;
  if (insertBeforeIndex >= assignments.length) return undefined;
  return assignments[insertBeforeIndex - 1]?.id;
}

export function CohortLessonPlanRail({
  assignments,
  cohortId,
  courseId,
  bearer,
  writable,
  bulkReleaseBusy,
  selectedAssignmentIds,
  selectableAssignments,
  isMutating,
  lessonIconForAssignment,
  onOrderSaved,
  onError,
  onToggleSelectAll,
  onToggleSelected,
  onInsert,
  onOpenDueModal,
  onAssign,
  onUnassign,
  onClose,
  onReopen,
  onDelete,
  onCopyStudentLink,
  onToggleHidden,
}: CohortLessonPlanRailProps) {
  const [orderedAssignments, setOrderedAssignments] = useState<TeacherCohortAssignmentRecord[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [insertBeforeIndex, setInsertBeforeIndex] = useState<number | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cohortPath = cohortId ? `/teacher/cohorts/${encodeURIComponent(cohortId)}` : undefined;

  useEffect(() => {
    setOrderedAssignments([...assignments]);
  }, [assignments]);

  const persistOrder = useCallback(
    async (nextOrder: TeacherCohortAssignmentRecord[]) => {
      if (!bearer || !cohortId || ordersMatch(nextOrder, assignments)) return;
      setSaveStatus("saving");
      onError(null);
      try {
        await putCohortAssignmentOrder(bearer, cohortId, assignmentIdsInOrder(nextOrder));
        await onOrderSaved();
        setSaveStatus("saved");
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 1800);
      } catch (err) {
        setSaveStatus("error");
        onError(err instanceof Error ? err.message : "Save order failed");
        setOrderedAssignments([...assignments]);
      }
    },
    [assignments, bearer, cohortId, onError, onOrderSaved]
  );

  const applyInsert = useCallback(
    (fromIndex: number, beforeIndex: number) => {
      const next = reorderLessonList(orderedAssignments, fromIndex, beforeIndex);
      setOrderedAssignments(next);
      if (!ordersMatch(next, assignments)) void persistOrder(next);
    },
    [assignments, orderedAssignments, persistOrder]
  );

  const allSelected =
    selectableAssignments.length > 0 &&
    selectableAssignments.every((a) => selectedAssignmentIds.has(a.id));

  const reorderEnabled = writable && saveStatus !== "saving";
  const isDragging = dragFromIndex !== null;

  const renderGap = (insertBeforeIndexValue: number, label: string) => (
    <SequenceInsertSlot
      key={`gap-${insertBeforeIndexValue}`}
      disabled={!writable || !courseId}
      label={label}
      onInsert={() => onInsert(insertAfterAssignmentIdForGap(orderedAssignments, insertBeforeIndexValue))}
      isDragging={isDragging}
      drag={
        reorderEnabled
          ? {
              insertBeforeIndex: insertBeforeIndexValue,
              isActive: insertBeforeIndex === insertBeforeIndexValue,
              onDragOverGap: setInsertBeforeIndex,
              onDropGap: (before) => {
                if (dragFromIndex !== null) applyInsert(dragFromIndex, before);
                setDragFromIndex(null);
                setInsertBeforeIndex(null);
              },
            }
          : undefined
      }
    />
  );

  return (
    <div className="teacher-cohort-plan-board">
      {writable ? (
        <div className="teacher-sequence-board-toolbar teacher-cohort-plan-toolbar">
          <p className="teacher-sequence-drag-hint">
            Grab a card and drop it on a glowing slot. Order saves automatically. Hover an icon to preview.
            The eye hides an item from students.
          </p>
          {saveStatus === "saving" ? (
            <span className="teacher-sequence-save-pill is-saving" role="status">
              Saving order…
            </span>
          ) : saveStatus === "saved" ? (
            <span className="teacher-sequence-save-pill is-saved" role="status">
              Order saved
            </span>
          ) : saveStatus === "error" ? (
            <span className="teacher-sequence-save-pill is-error" role="status">
              Couldn&apos;t save. Try dragging again
            </span>
          ) : null}
        </div>
      ) : null}
      <div
        className={`teacher-cohort-plan-header${writable ? "" : " teacher-cohort-plan-header--readonly"}`}
        aria-hidden
      >
        {writable ? (
          <span className="teacher-cohort-plan-header-check">
            <input
              type="checkbox"
              aria-label="Select all items"
              checked={allSelected}
              disabled={bulkReleaseBusy || selectableAssignments.length === 0}
              onChange={onToggleSelectAll}
            />
          </span>
        ) : null}
        {writable ? <span className="teacher-cohort-plan-header-grip" aria-hidden /> : null}
        <span className="teacher-cohort-plan-header-step" aria-hidden />
        <span className="teacher-cohort-plan-header-icon" aria-hidden />
        <span className="teacher-cohort-plan-header-label teacher-cohort-plan-header-label--lesson">Item</span>
        <span className="teacher-cohort-plan-header-label teacher-cohort-plan-header-label--lifecycle">Lifecycle</span>
        <span className="teacher-cohort-plan-header-label teacher-cohort-plan-header-label--due">Due</span>
        <span className="teacher-cohort-plan-header-label teacher-cohort-plan-header-label--show">Show</span>
        {writable ? (
          <span className="teacher-cohort-plan-header-label teacher-cohort-plan-header-label--actions">Actions</span>
        ) : null}
        {writable ? <span className="teacher-cohort-plan-header-remove" aria-hidden /> : null}
      </div>

      <ol
        className={`teacher-sequence-list${isDragging ? " is-dragging" : ""}`}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setInsertBeforeIndex(null);
          }
        }}
      >
        {writable && orderedAssignments.length === 0 ? renderGap(0, "Add first item") : null}
        {orderedAssignments.map((a, index) => {
          const isQuiz = a.itemKind === "quiz" && a.quizId;
          const isPresentation = a.itemKind === "presentation" && a.presentationId;
          const icons = lessonIconForAssignment(a);
          const lifecycle = assignmentLifecycleLabel(a);
          const mutating = isMutating(a.id);
          return (
            <Fragment key={a.id}>
              {writable ? renderGap(index, index === 0 ? "Add at start" : `Add before ${a.lessonTitle}`) : null}
              <li
                className={`teacher-cohort-plan-card${writable ? "" : " teacher-cohort-plan-card--readonly"}${
                  a.hidden ? " is-hidden-from-students" : ""
                }${dragFromIndex === index ? " is-source" : ""}${
                  isDragging && dragFromIndex !== index ? " is-idle-during-drag" : ""
                }`}
              >
                {writable ? (
                  <span className="teacher-cohort-plan-card-check">
                    {!a.closed ? (
                      <input
                        type="checkbox"
                        aria-label={`Select ${a.lessonTitle}`}
                        checked={selectedAssignmentIds.has(a.id)}
                        disabled={bulkReleaseBusy || mutating}
                        onChange={() => onToggleSelected(a.id)}
                      />
                    ) : null}
                  </span>
                ) : null}
                {writable ? (
                  <div
                    className="teacher-sequence-card-grip"
                    draggable={reorderEnabled && !mutating}
                    aria-label={`Drag to reorder ${a.lessonTitle}`}
                    title="Drag to reorder"
                    onDragStart={(e) => {
                      setDragFromIndex(index);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => {
                      setDragFromIndex(null);
                      setInsertBeforeIndex(null);
                    }}
                  >
                    <FontAwesomeIcon icon={faGripVertical} fixedWidth />
                  </div>
                ) : null}
                <span className="teacher-sequence-card-step" aria-hidden>
                  {index + 1}
                </span>
                <span className="teacher-cohort-plan-card-icon">
                  {isQuiz && a.quizId ? (
                    <QuizPreviewTile
                      quizId={a.quizId}
                      quizTitle={a.lessonTitle}
                      courseId={courseId}
                      cohortId={cohortId}
                      returnTo={cohortPath}
                      iconName={icons.iconName}
                      iconBgColor={icons.iconBgColor}
                      iconFgColor={icons.iconFgColor}
                      size="sm"
                    />
                  ) : isPresentation && a.presentationId ? (
                    <PresentationPreviewTile
                      presentationId={a.presentationId}
                      presentationTitle={a.lessonTitle}
                      courseId={courseId}
                      cohortId={cohortId}
                      returnTo={cohortPath}
                      iconName={icons.iconName}
                      iconBgColor={icons.iconBgColor}
                      iconFgColor={icons.iconFgColor}
                      size="sm"
                    />
                  ) : a.lessonId ? (
                    <LessonPreviewTile
                      lessonId={a.lessonId}
                      lessonTitle={a.lessonTitle}
                      courseId={a.courseId}
                      cohortId={cohortId}
                      returnTo={cohortPath}
                      iconName={icons.iconName}
                      iconBgColor={icons.iconBgColor}
                      iconFgColor={icons.iconFgColor}
                      workspaceMode={icons.workspaceMode}
                      size="sm"
                    />
                  ) : null}
                </span>
                <span className="teacher-cohort-plan-card-title">{a.lessonTitle}</span>
                <span className="teacher-cohort-plan-card-lifecycle">
                  <span className={`badge ${lifecycle === "Planned" ? "badge-muted" : ""}`}>{lifecycle}</span>
                </span>
                <span className="teacher-cohort-plan-card-due">
                  {renderDueCell(a, writable, mutating, onOpenDueModal)}
                </span>
                <button
                  type="button"
                  className={`teacher-cohort-plan-visibility${a.hidden ? " is-hidden" : ""}`}
                  aria-pressed={Boolean(a.hidden)}
                  aria-label={
                    a.hidden
                      ? `Show ${a.lessonTitle} to students`
                      : `Hide ${a.lessonTitle} from students`
                  }
                  title={a.hidden ? "Hidden from students. Click to show." : "Visible to students. Click to hide."}
                  disabled={!writable || mutating}
                  onClick={() => onToggleHidden(a)}
                >
                  <FontAwesomeIcon icon={a.hidden ? faEyeSlash : faEye} fixedWidth />
                </button>
                {writable ? (
                  <CohortAssignmentActionsMenu
                    assignment={a}
                    disabled={mutating}
                    resultsHref={
                      isQuiz && a.quizId && cohortId
                        ? `/teacher/cohorts/${encodeURIComponent(cohortId)}/quizzes/${encodeURIComponent(a.quizId)}`
                        : undefined
                    }
                    onAssign={() => onAssign(a)}
                    onUnassign={() => onUnassign(a)}
                    onClose={() => onClose(a)}
                    onReopen={() => onReopen(a)}
                    onDelete={() => onDelete(a)}
                    onCopyStudentLink={() => onCopyStudentLink(a)}
                  />
                ) : (
                  <span />
                )}
                {writable ? (
                  <button
                    type="button"
                    className="teacher-sequence-card-remove"
                    disabled={mutating}
                    onClick={() => onDelete(a)}
                    title="Remove from plan"
                    aria-label={`Remove ${a.lessonTitle} from plan`}
                  >
                    <FontAwesomeIcon icon={faXmark} fixedWidth />
                  </button>
                ) : null}
              </li>
            </Fragment>
          );
        })}
        {writable && orderedAssignments.length > 0
          ? renderGap(orderedAssignments.length, "Add at end")
          : null}
      </ol>
    </div>
  );
}
